import type { Frame, MatOption } from '@shared/types/firestore';

export const DEFAULT_MAT_COLOR_HEX = '#FFFFFF';
export const FRAME_OFFSET_MIN = -2;
export const FRAME_OFFSET_MAX = 6;
export const FRAME_OFFSET_STEP = 0.25;

const STANDARD_MAT_OPTIONS: MatOption[] = [
  { id: 'standard-white', name: 'White', hexColor: '#FFFFFF', isDefault: true },
  { id: 'standard-black', name: 'Black', hexColor: '#000000' },
];

export function normalizeMatColorHex(value?: string | null): string {
  const sanitized = value?.trim().replace(/^#/, '') ?? '';

  if (/^[0-9a-fA-F]{3}$/.test(sanitized)) {
    return `#${sanitized
      .split('')
      .map(character => `${character}${character}`)
      .join('')}`.toUpperCase();
  }

  if (/^[0-9a-fA-F]{6}$/.test(sanitized)) {
    return `#${sanitized.toUpperCase()}`;
  }

  return DEFAULT_MAT_COLOR_HEX;
}

export function clampFrameOffset(value?: number | null): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }

  const clamped = Math.min(FRAME_OFFSET_MAX, Math.max(FRAME_OFFSET_MIN, value));
  return Math.round(clamped / FRAME_OFFSET_STEP) * FRAME_OFFSET_STEP;
}

export function formatFrameOffset(value: number): string {
  const normalized = clampFrameOffset(value);
  return normalized === 0 ? 'Default' : `${normalized > 0 ? '+' : ''}${normalized.toFixed(2)} in`;
}

export function getFrameMatOptions(frame: Frame): MatOption[] {
  const options = [...STANDARD_MAT_OPTIONS, ...(frame.matOptions ?? [])];
  const uniqueColors = new Set<string>();

  return options.flatMap(option => {
    const hexColor = normalizeMatColorHex(option.hexColor);
    if (uniqueColors.has(hexColor)) {
      return [];
    }

    uniqueColors.add(hexColor);
    return [{ ...option, hexColor }];
  });
}

export function resolveFrameMatOption(frame: Frame, preferredColor?: string | null): MatOption {
  const options = getFrameMatOptions(frame);
  const configuredDefault = frame.matOptions?.find(option => option.isDefault)?.hexColor;
  const targetColor = normalizeMatColorHex(
    preferredColor ?? frame.defaultMatColor ?? configuredDefault ?? DEFAULT_MAT_COLOR_HEX
  );
  const matchingOption = options.find(option => option.hexColor === targetColor);

  if (matchingOption) {
    return matchingOption;
  }

  return {
    id: `custom-${targetColor.slice(1).toLowerCase()}`,
    name: 'Custom',
    hexColor: targetColor,
  };
}
